import { Injectable, inject, signal } from '@angular/core';
import { CeSnackbarService, CeSnackbarState } from '@kempower-nx-workspace/shared/kp-design-system';

type Timeout = ReturnType<typeof setTimeout>;

const ONLINE_SNACKBAR_DELAY_MS = 700;
const ONLINE_SNACKBAR_DURATION_MS = 5000;

@Injectable({ providedIn: 'root' })
export class NetworkStatusService {
    readonly #snackbar = inject(CeSnackbarService);

    isOnline = signal(navigator.onLine);
    #hasInitialized = false;
    #timeOutStore!: Timeout | null;

    initOfflineOnline(): void {
        if (this.#hasInitialized) return;

        this.#hasInitialized = true;

        window.addEventListener('online', () => {
            this.isOnline.set(true);
            this.#snackbar.closeSnackbar();

            this.#timeOutStore = setTimeout(() => {
                if (!this.isOnline()) return;

                this.#openOnlineSnackbar();
            }, ONLINE_SNACKBAR_DELAY_MS);
        });

        window.addEventListener('offline', () => {
            this.isOnline.set(false);

            if (this.#timeOutStore) {
                clearTimeout(this.#timeOutStore);
                this.#timeOutStore = null;
            }

            this.#openOfflineSnackbar();
        });
    }

    #openOfflineSnackbar(): void {
        this.#snackbar.showSnackbar({
            message: "Your internet connection is down - we're watching for it to come back.",
            duration: 'persist',
            state: CeSnackbarState.ERROR,
            icon: 'offline',
        });
    }

    #openOnlineSnackbar(): void {
        this.#snackbar.showSnackbar({
            message: 'Internet connection restored ',
            duration: ONLINE_SNACKBAR_DURATION_MS,
            icon: 'checkmark',
        });
    }
}
